/**
 * "Calculate / set values" (ADR-0103): the plain recipes the Builder offers ("Add VAT",
 * "Add working days", "Within a tolerance", …), how each becomes a formula of the spec's
 * expression language, and how a formula reads back as a recipe so the step opens in the plain
 * editor again. Anything else is a written formula; the control plane works out sample values
 * (`POST /v1/specs/formulas/try`) with the same engine the run uses.
 */
import type { Text } from "../types";
import type { AgentSpec, SpecNode } from "./types";

/** One value a "Calculate" step works out. */
export interface CalcValue {
  key: string;
  expr: string;
  label?: Text | null;
}

export type RecipeGroup = "numbers" | "money" | "compare" | "dates" | "hijri" | "text" | "set";
/** What an input of a recipe takes: a value from earlier (or typed), or a setting. */
export type RecipeArgKind = "value" | "list" | "field" | "number" | "rate" | "tolerance" | "places" | "currency" | "text" | "lang" | "pattern" | "constant";
export type FormulaKind = "number" | "integer" | "date" | "text" | "boolean" | "list" | "object" | "any";

export interface Recipe {
  id: string;
  group: RecipeGroup;
  /** The function it calls, or the operator (`+ - * /`), or `copy` / `constant`. */
  fn: string;
  args: { name: string; kind: RecipeArgKind; optional?: boolean }[];
  returns: FormulaKind;
}

const R = (id: string, group: RecipeGroup, fn: string, args: Recipe["args"], returns: FormulaKind): Recipe => ({ id, group, fn, args, returns });
const v = (name: string, optional = false) => ({ name, kind: "value" as RecipeArgKind, ...(optional ? { optional } : {}) });
const k = (name: string, kind: RecipeArgKind, optional = false) => ({ name, kind, ...(optional ? { optional } : {}) });

export const RECIPES: readonly Recipe[] = [
  R("add", "numbers", "+", [v("a"), v("b")], "number"),
  R("subtract", "numbers", "-", [v("a"), v("b")], "number"),
  R("multiply", "numbers", "*", [v("a"), v("b")], "number"),
  R("divide", "numbers", "/", [v("a"), v("b")], "number"),
  R("round", "numbers", "round", [v("a"), k("places", "places")], "number"),
  R("sum", "numbers", "sum", [k("list", "list"), k("field", "field", true)], "number"),
  R("avg", "numbers", "avg", [k("list", "list"), k("field", "field", true)], "number"),
  R("count", "numbers", "count", [k("list", "list")], "integer"),
  R("percent", "numbers", "percent", [v("a"), v("b")], "number"),
  R("percent_of", "numbers", "percent_of", [k("rate", "rate"), v("a")], "number"),
  R("percent_change", "numbers", "percent_change", [v("a"), v("b")], "number"),
  R("vat", "money", "vat", [v("a"), k("rate", "rate")], "number"),
  R("with_vat", "money", "with_vat", [v("a"), k("rate", "rate")], "number"),
  R("without_vat", "money", "without_vat", [v("a"), k("rate", "rate")], "number"),
  R("vat_in", "money", "vat_in", [v("a"), k("rate", "rate")], "number"),
  R("format_money", "money", "format_money", [v("a"), k("currency", "currency"), k("lang", "lang", true)], "text"),
  R("within", "compare", "within", [v("a"), v("b"), k("tolerance", "tolerance")], "boolean"),
  R("today", "dates", "today", [], "date"),
  R("add_days", "dates", "add_days", [v("a"), k("days", "number")], "date"),
  R("add_working_days", "dates", "add_working_days", [v("a"), k("days", "number")], "date"),
  R("add_months", "dates", "add_months", [v("a"), k("months", "number")], "date"),
  R("days_between", "dates", "days_between", [v("a"), v("b")], "integer"),
  R("working_days_between", "dates", "working_days_between", [v("a"), v("b")], "integer"),
  R("month_end", "dates", "month_end", [v("a")], "date"),
  R("format_date", "dates", "format_date", [v("a"), k("pattern", "pattern"), k("lang", "lang", true)], "text"),
  R("to_hijri", "hijri", "to_hijri", [v("a")], "text"),
  R("format_hijri", "hijri", "format_hijri", [v("a"), k("lang", "lang", true)], "text"),
  R("from_hijri", "hijri", "from_hijri", [v("a")], "date"),
  R("add_hijri_months", "hijri", "add_hijri_months", [v("a"), k("months", "number")], "date"),
  R("concat", "text", "concat", [v("a"), v("b"), v("c", true)], "text"),
  R("pad", "text", "pad", [v("a"), k("width", "number")], "text"),
  R("upper", "text", "upper", [v("a")], "text"),
  R("lower", "text", "lower", [v("a")], "text"),
  R("trim", "text", "trim", [v("a")], "text"),
  R("replace", "text", "replace", [v("a"), k("find", "text"), k("with", "text")], "text"),
  R("format_number", "text", "format_number", [v("a"), k("places", "places")], "text"),
  R("copy", "set", "copy", [v("a")], "any"),
  R("constant", "set", "constant", [k("value", "constant")], "any"),
];

export const RECIPE_GROUPS: readonly RecipeGroup[] = ["numbers", "money", "compare", "dates", "hijri", "text", "set"];

/** An input's value: a value from earlier steps, or something typed. */
export type FormulaArg = { path: string } | { value: string | number | boolean };

export function recipe(id: string): Recipe | undefined {
  return RECIPES.find((r) => r.id === id);
}

const isPath = (a: FormulaArg | undefined): a is { path: string } => !!a && "path" in a && typeof a.path === "string" && a.path !== "";

function literal(x: string | number | boolean): string {
  if (typeof x === "number") return Number.isFinite(x) ? String(x) : "0";
  if (typeof x === "boolean") return x ? "true" : "false";
  // A typed number in a value box is a number ("15", "1,200.5").
  const n = numberText(x);
  if (n !== null) return n;
  return JSON.stringify(x);
}

/** A typed number ("1,200.50", "١٥") as formula text, else null. */
export function numberText(text: string): string | null {
  const s = text
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/٫/g, ".")
    .replace(/[,٬\s]/g, "")
    .trim();
  return /^-?(\d+(\.\d+)?|\.\d+)$/.test(s) ? String(Number(s)) : null;
}

function argText(a: FormulaArg | undefined, kind: RecipeArgKind): string | null {
  if (a === undefined) return null;
  if (isPath(a)) return `state.${a.path}`;
  if (!("value" in a) || a.value === "" || a.value === undefined) return null;
  if (kind === "field" || kind === "currency" || kind === "lang" || kind === "pattern" || kind === "text") return JSON.stringify(String(a.value));
  if (kind === "tolerance") {
    const s = String(a.value).trim();
    return numberText(s) ?? JSON.stringify(s);
  }
  if (kind === "constant") return literal(a.value);
  return literal(a.value);
}

/** The formula of a recipe with its inputs; null while a needed input is missing. */
export function buildFormula(id: string, args: Record<string, FormulaArg | undefined>): string | null {
  const r = recipe(id);
  if (!r) return null;
  const parts: string[] = [];
  for (const a of r.args) {
    const text = argText(args[a.name], a.kind);
    if (text === null) {
      if (a.optional) break;
      return null;
    }
    parts.push(text);
  }
  if (r.fn === "copy" || r.fn === "constant") return parts[0] ?? null;
  if (["+", "-", "*", "/"].includes(r.fn)) return `${parts[0]} ${r.fn} ${parts[1]}`;
  return `${r.fn}(${parts.join(", ")})`;
}

// ---------------------------------------------------------------- reading a formula back

type Tok = { t: "path" | "num" | "str" | "name" | "op" | "(" | ")" | ","; v: string };

function tokens(src: string): Tok[] | null {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i] as string;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (c === "(" || c === ")" || c === ",") {
      out.push({ t: c, v: c });
      i++;
      continue;
    }
    if ("+-*/".includes(c)) {
      // A minus right before a number at the start or after an operator/comma is a sign.
      const prev = out[out.length - 1];
      if (c === "-" && /[\d.]/.test(src[i + 1] ?? "") && (!prev || prev.t === "op" || prev.t === "(" || prev.t === ",")) {
        const m = /^-\d+(\.\d+)?|^-\.\d+/.exec(src.slice(i));
        if (m) {
          out.push({ t: "num", v: m[0] });
          i += m[0].length;
          continue;
        }
      }
      out.push({ t: "op", v: c });
      i++;
      continue;
    }
    if (c === '"' || c === "'") {
      let j = i + 1;
      let s = "";
      while (j < src.length && src[j] !== c) {
        if (src[j] === "\\") {
          const n = src[j + 1];
          if (n === "u") {
            s += String.fromCharCode(parseInt(src.slice(j + 2, j + 6), 16));
            j += 6;
            continue;
          }
          s += n === "n" ? "\n" : (n ?? "");
          j += 2;
          continue;
        }
        s += src[j];
        j++;
      }
      if (j >= src.length) return null;
      out.push({ t: "str", v: s });
      i = j + 1;
      continue;
    }
    const num = /^\d+(\.\d+)?|^\.\d+/.exec(src.slice(i));
    if (num) {
      out.push({ t: "num", v: num[0] });
      i += num[0].length;
      continue;
    }
    const path = /^state((\.[A-Za-z_]\w*)|(\[\d+\])|(\[(["'])[^"'\]]*\5\]))+/.exec(src.slice(i));
    if (path) {
      out.push({ t: "path", v: path[0].slice("state.".length).replace(/\[(["'])([^"'\]]*)\1\]/g, ".$2") });
      i += path[0].length;
      continue;
    }
    const name = /^[A-Za-z_]\w*/.exec(src.slice(i));
    if (name) {
      out.push({ t: "name", v: name[0] });
      i += name[0].length;
      continue;
    }
    return null;
  }
  return out;
}

function atom(t: Tok | undefined): FormulaArg | null {
  if (!t) return null;
  if (t.t === "path") return { path: t.v };
  if (t.t === "num") return { value: Number(t.v) };
  if (t.t === "str") return { value: t.v };
  if (t.t === "name" && (t.v === "true" || t.v === "false")) return { value: t.v === "true" };
  return null;
}

/** The recipe and inputs a formula was made from, or null for a written formula. */
export function parseFormula(expr: string): { recipe: string; args: Record<string, FormulaArg> } | null {
  const ts = tokens(expr.trim());
  if (!ts || !ts.length) return null;
  const named = (r: Recipe, list: FormulaArg[]) => Object.fromEntries(r.args.slice(0, list.length).map((a, i) => [a.name, list[i] as FormulaArg]));
  // A single value: copy (a value from earlier) or a constant.
  if (ts.length === 1) {
    const a = atom(ts[0]);
    if (!a) return null;
    return isPath(a) ? { recipe: "copy", args: { a } } : { recipe: "constant", args: { value: a } };
  }
  // a OP b
  if (ts.length === 3 && ts[1]?.t === "op") {
    const a = atom(ts[0]);
    const b = atom(ts[2]);
    const r = RECIPES.find((x) => x.fn === ts[1]?.v);
    if (a && b && r) return { recipe: r.id, args: { a, b } };
    return null;
  }
  // fn(atom, atom, …)
  if (ts[0]?.t === "name" && ts[1]?.t === "(" && ts[ts.length - 1]?.t === ")") {
    const inner = ts.slice(2, -1);
    const list: FormulaArg[] = [];
    for (let i = 0; i < inner.length; i += 2) {
      const a = atom(inner[i]);
      if (!a) return null;
      if (i + 1 < inner.length && inner[i + 1]?.t !== ",") return null;
      list.push(a);
    }
    const r = RECIPES.find((x) => x.fn === ts[0]?.v && x.fn !== "copy" && x.fn !== "constant");
    if (!r) return null;
    const needed = r.args.filter((a) => !a.optional).length;
    if (list.length < needed || list.length > r.args.length) return null;
    return { recipe: r.id, args: named(r, list) };
  }
  return null;
}

/** The state paths a formula reads (without `state.`), in order. */
export function formulaRefs(expr: string): string[] {
  const out: string[] = [];
  for (const t of tokens(expr) ?? []) if (t.t === "path" && !out.includes(t.v)) out.push(t.v);
  if (!tokens(expr)) for (const m of expr.matchAll(/\bstate\.([A-Za-z_]\w*(?:\.[A-Za-z_]\w*|\[\d+\])*)/g)) if (!out.includes(m[1] as string)) out.push(m[1] as string);
  return out;
}

/** What kind of value a formula gives, as far as can be told without the values. */
export function formulaKind(expr: string): FormulaKind {
  const p = parseFormula(expr);
  if (p) {
    const r = recipe(p.recipe);
    if (r?.id === "constant") {
      const c = (p.args.value as { value?: unknown } | undefined)?.value;
      return typeof c === "number" ? (Number.isInteger(c) ? "integer" : "number") : typeof c === "boolean" ? "boolean" : "text";
    }
    if (r && r.returns !== "any") return r.returns;
    return "any";
  }
  const ts = tokens(expr.trim()) ?? [];
  if (ts.some((t) => t.t === "op" && t.v === "+") && ts.some((t) => t.t === "str")) return "text";
  if (ts.some((t) => t.t === "name" && ["within", "contains", "is_empty", "starts_with", "ends_with", "is_working_day"].includes(t.v) && ts.indexOf(t) === 0)) return "boolean";
  if (/(==|!=|<=|>=|<|>|\band\b|\bor\b|\bnot\b)/.test(expr)) return "boolean";
  const first = ts[0];
  if (first?.t === "name" && ts[1]?.t === "(" && ts[ts.length - 1]?.t === ")") {
    const r = RECIPES.find((x) => x.fn === first.v);
    if (r) return r.returns;
    if (["round", "round_up", "round_down", "abs", "number", "min", "max"].includes(first.v)) return "number";
    if (["date", "add_years", "month_start"].includes(first.v)) return "date";
  }
  if (ts.some((t) => t.t === "op")) return "number";
  return "any";
}

const KIND_SCHEMA: Record<FormulaKind, Record<string, unknown>> = {
  number: { type: "number" },
  integer: { type: "integer" },
  date: { type: "string", format: "date" },
  text: { type: "string" },
  boolean: { type: "boolean" },
  list: { type: "array" },
  object: { type: "object" },
  any: {},
};

export function formulaSchema(expr: string): Record<string, unknown> {
  return { ...KIND_SCHEMA[formulaKind(expr)] };
}

/** A value name from its plain English name ("Total with VAT" → `total_with_vat`). */
export function valueKey(label: string, taken: readonly string[] = []): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^(\d)/, "v_$1")
      .slice(0, 40) || "value";
  const reserved = ["messages", "tool_call", "visits", "lang", "previous", "document_ids"];
  let key = reserved.includes(base) ? `${base}_value` : base;
  for (let i = 2; taken.includes(key); i++) key = `${base}_${i}`;
  return key;
}

/** The values of a Calculate step (empty for other steps). */
export function calcValues(n: SpecNode | undefined): CalcValue[] {
  return n?.type === "calculate" && Array.isArray(n.values) ? (n.values as CalcValue[]) : [];
}

/** Every value the agent's Calculate steps work out (key → step id). */
export function calculatedKeys(spec: AgentSpec): Record<string, string> {
  const out: Record<string, string> = {};
  for (const n of spec.spec.graph.nodes) for (const c of calcValues(n)) out[c.key] = n.id;
  return out;
}

/** "Within 2%" in a rule (routers and arrows): the condition, and reading it back. */
export function withinCondition(a: string, b: FormulaArg, tolerance: string): string {
  const other = isPath(b) ? `state.${b.path}` : literal(b.value);
  const tol = numberText(tolerance) ?? JSON.stringify(tolerance.trim());
  return `within(state.${a}, ${other}, ${tol})`;
}

export function parseWithin(when: string): { a: string; b: FormulaArg; tolerance: string; negated: boolean } | null {
  const text = when.trim();
  const negated = /^not\s+/.test(text);
  const p = parseFormula(negated ? text.replace(/^not\s+/, "") : text);
  if (!p || p.recipe !== "within") return null;
  const a = p.args.a;
  const b = p.args.b;
  const tol = p.args.tolerance;
  if (!isPath(a) || !b || !tol || !("value" in tol)) return null;
  return { a: a.path, b, tolerance: String(tol.value), negated };
}

/** `POST /v1/specs/formulas/try`: values worked out in order, and a condition, on sample values. */
export interface FormulaTryIn {
  values?: { key: string; expr: string }[];
  condition?: string | null;
  sample: Record<string, unknown>;
}
export interface FormulaTried {
  key: string;
  value: unknown;
  schema?: Record<string, unknown>;
  /** The reason in plain (English) words, when it could not be worked out. */
  error?: string;
  kind?: "not_allowed" | "cannot_work_out";
}
export interface FormulaTryOut {
  values: FormulaTried[];
  condition?: { value: boolean | null; error?: string; kind?: "not_allowed" };
}
